// ─────────────────────────────────────────────────────────────────
// Glass-i — Item Master API client (Stage 6B.8A-3).
//
// Thin axios wrappers around `/api/erp/masters/items/*` and its six
// extension endpoints (glass / profile / hardware / jobwork / ecommerce
// / images). Mirrors the customerApi / uomApi shape so the same
// envelope unwrap, grid-field translation, and approval-MFA header
// conventions are reused.
//
// This client is the single integration surface the Product Creation
// Studio talks to — the studio orchestrates create/update of the slim
// core inventory_items row first, then routes through the matching
// extension endpoint for any enabled technical / ecommerce / image
// section. Default item list/grid does NOT touch extension tables.
// ─────────────────────────────────────────────────────────────────

import client from '../../../../api/client';
import { categoryApi, materialTypeApi, polishTypeApi, brandApi } from '../itemConfig/itemConfigApi';
import { uomApi } from '../uoms/uomApi';
import {
  mapField as helperMapField,
  translateQuery as helperTranslateQuery
} from './itemMasterHelpers';

const BASE = '/api/erp/masters/items';

function unwrap(resp) {
  return resp && resp.data && resp.data.data !== undefined
    ? resp.data.data
    : resp && resp.data;
}

// Stage 6B.8A-4: the camelCase→snake_case grid-field map and the
// `translateQuery` helper now live in `itemMasterHelpers.js` (plain
// JS) so node:test can exercise them without resolving the axios
// chain. We re-export them here so existing callers keep working.
export const mapField       = helperMapField;
export const translateQuery = helperTranslateQuery;

// ── Core item endpoints ──────────────────────────────────────────
export const itemsApi = {
  list: (query) =>
    client.post(`${BASE}/list`, translateQuery(query) || {}).then(unwrap),
  lookup: (params = {}) =>
    client.get(`${BASE}/lookup`, { params }).then(unwrap),
  get: (id) =>
    client.get(`${BASE}/${encodeURIComponent(id)}`).then(unwrap),
  create: (payload) =>
    client.post(BASE, payload).then(unwrap),
  update: (id, payload) =>
    client.put(`${BASE}/${encodeURIComponent(id)}`, payload).then(unwrap),
  updateStatus: (id, body) =>
    client.patch(`${BASE}/${encodeURIComponent(id)}/status`, body).then(unwrap),
  remove: (id) =>
    client.delete(`${BASE}/${encodeURIComponent(id)}`).then(unwrap),

  // ── Stage 6B.8A-2C — glass-spec extension ─────────────────────
  getGlassSpec: (itemId) =>
    client.get(`${BASE}/${encodeURIComponent(itemId)}/glass-spec`).then(unwrap),
  upsertGlassSpec: (itemId, payload) =>
    client.put(`${BASE}/${encodeURIComponent(itemId)}/glass-spec`, payload).then(unwrap),
  deleteGlassSpec: (itemId) =>
    client.delete(`${BASE}/${encodeURIComponent(itemId)}/glass-spec`).then(unwrap),

  // ── Stage 6B.8A-2D — profile-spec extension ───────────────────
  getProfileSpec: (itemId) =>
    client.get(`${BASE}/${encodeURIComponent(itemId)}/profile-spec`).then(unwrap),
  upsertProfileSpec: (itemId, payload) =>
    client.put(`${BASE}/${encodeURIComponent(itemId)}/profile-spec`, payload).then(unwrap),
  deleteProfileSpec: (itemId) =>
    client.delete(`${BASE}/${encodeURIComponent(itemId)}/profile-spec`).then(unwrap),

  // ── Stage 6B.8A-2E — hardware-spec extension ──────────────────
  getHardwareSpec: (itemId) =>
    client.get(`${BASE}/${encodeURIComponent(itemId)}/hardware-spec`).then(unwrap),
  upsertHardwareSpec: (itemId, payload) =>
    client.put(`${BASE}/${encodeURIComponent(itemId)}/hardware-spec`, payload).then(unwrap),
  deleteHardwareSpec: (itemId) =>
    client.delete(`${BASE}/${encodeURIComponent(itemId)}/hardware-spec`).then(unwrap),

  // ── Stage 6B.8A-2F — jobwork-spec extension ───────────────────
  getJobworkSpec: (itemId) =>
    client.get(`${BASE}/${encodeURIComponent(itemId)}/jobwork-spec`).then(unwrap),
  upsertJobworkSpec: (itemId, payload) =>
    client.put(`${BASE}/${encodeURIComponent(itemId)}/jobwork-spec`, payload).then(unwrap),
  deleteJobworkSpec: (itemId) =>
    client.delete(`${BASE}/${encodeURIComponent(itemId)}/jobwork-spec`).then(unwrap),

  // ── Stage INTERLAYER-STOCK-1 — interlayer-spec (PVB/EVA/SGP) ───
  // listInterlayers → items that HAVE an interlayer spec (standalone page).
  listInterlayers: (params = {}) =>
    client.get(`${BASE}/interlayers`, { params }).then(unwrap),
  getInterlayerSpec: (itemId) =>
    client.get(`${BASE}/${encodeURIComponent(itemId)}/interlayer-spec`).then(unwrap),
  upsertInterlayerSpec: (itemId, payload) =>
    client.put(`${BASE}/${encodeURIComponent(itemId)}/interlayer-spec`, payload).then(unwrap),
  deleteInterlayerSpec: (itemId) =>
    client.delete(`${BASE}/${encodeURIComponent(itemId)}/interlayer-spec`).then(unwrap),

  // ── Stage DGU-CONSUMABLE-1 — DGU consumables (spacer/desiccant/…) ──
  // listDguConsumables → items that HAVE a spec; ?kind=spacer for the DGU popup.
  listDguConsumables: (params = {}) =>
    client.get(`${BASE}/dgu-consumables`, { params }).then(unwrap),
  getDguConsumableSpec: (itemId) =>
    client.get(`${BASE}/${encodeURIComponent(itemId)}/dgu-consumable-spec`).then(unwrap),
  upsertDguConsumableSpec: (itemId, payload) =>
    client.put(`${BASE}/${encodeURIComponent(itemId)}/dgu-consumable-spec`, payload).then(unwrap),
  deleteDguConsumableSpec: (itemId) =>
    client.delete(`${BASE}/${encodeURIComponent(itemId)}/dgu-consumable-spec`).then(unwrap),

  // ── Stage 6B.8A-2G — ecommerce product profile ────────────────
  getEcommerceProfile: (itemId) =>
    client.get(`${BASE}/${encodeURIComponent(itemId)}/ecommerce-profile`).then(unwrap),
  upsertEcommerceProfile: (itemId, payload) =>
    client.put(`${BASE}/${encodeURIComponent(itemId)}/ecommerce-profile`, payload).then(unwrap),
  deleteEcommerceProfile: (itemId) =>
    client.delete(`${BASE}/${encodeURIComponent(itemId)}/ecommerce-profile`).then(unwrap),

  // ── Stage 6B.8A-2H — inventory item images (1:N) ──────────────
  listImages: (itemId) =>
    client.get(`${BASE}/${encodeURIComponent(itemId)}/images`).then(unwrap),
  createImage: (itemId, payload) =>
    client.post(`${BASE}/${encodeURIComponent(itemId)}/images`, payload).then(unwrap),
  updateImage: (itemId, imageId, payload) =>
    client.put(
      `${BASE}/${encodeURIComponent(itemId)}/images/${encodeURIComponent(imageId)}`,
      payload
    ).then(unwrap),
  deleteImage: (itemId, imageId) =>
    client.delete(
      `${BASE}/${encodeURIComponent(itemId)}/images/${encodeURIComponent(imageId)}`
    ).then(unwrap),
  setImagePrimary: (itemId, imageId) =>
    client.patch(
      `${BASE}/${encodeURIComponent(itemId)}/images/${encodeURIComponent(imageId)}/primary`,
      {}
    ).then(unwrap),

  // ── Stage 6B.8A-3H — item sets / kits ─────────────────────────
  // GET returns { set, components } or null when no set exists.
  // PUT fully replaces the component list (upsert). DELETE removes
  // the set definition only; the parent inventory_items row is never
  // touched. Reuses items.view / items.update permissions.
  getItemSet: (itemId) =>
    client.get(`${BASE}/${encodeURIComponent(itemId)}/set`).then(unwrap),
  upsertItemSet: (itemId, payload) =>
    client.put(`${BASE}/${encodeURIComponent(itemId)}/set`, payload).then(unwrap),
  deleteItemSet: (itemId) =>
    client.delete(`${BASE}/${encodeURIComponent(itemId)}/set`).then(unwrap)
};

// ── Lookup re-exports — colocated so steps don't need to know
//    where category/material/UOM lookups live. The Studio uses these
//    to populate the Classification + UOM/GST dropdowns. Existing
//    backend endpoints — no new RBAC or routes.
export const studioLookups = {
  itemCategories: (params = {}) => categoryApi.lookup(params),
  materialTypes:  (params = {}) => materialTypeApi.lookup(params),
  polishTypes:    (params = {}) => polishTypeApi.lookup(params),
  brands:         (params = {}) => brandApi.lookup(params),
  uoms:           (params = {}) => uomApi.lookup(params),

  // Stage 6B.8A-3F — GST rate picker. Reads the existing tenant
  // tax_rates master so item-create popups can map a UUID + snapshot
  // the rate instead of letting an operator type a free-text percent.
  taxRates:       ({ q = '', limit = 100 } = {}) =>
    client.get('/api/erp/accounts/tax-rates/lookup', { params: { q, limit } })
      .then((r) => (r && r.data && r.data.data && Array.isArray(r.data.data.rows))
        ? r.data.data.rows
        : []),

  // Source stock items — uses the same /api/erp/masters/items/lookup
  // endpoint but defaults to is_stock_item: true so the picker shows
  // base raw materials. The current item id is sent so the Studio can
  // filter it out client-side (DB CHECK rejects self-link anyway).
  sourceStockItems: ({ q = '', limit = 50 } = {}) =>
    itemsApi.lookup({ q, isStockItem: true, status: 'active', limit })
};

export default itemsApi;
